import React, { FC, PropsWithChildren, ReactNode } from 'react';
import styled from 'styled-components';
import { border } from '../common/theme/constants';
import KitSkipToContent from './KitSkipToContent';
import { PageWrapper, TranslateFunc } from '../common/types';

// https://dev.to/niorad/keeping-the-footer-at-the-bottom-with-css-grid-15mf
const Container = styled.div`
  min-height: 100%;
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-template-columns: 100%;
`;

const Header = styled.header`
  background-color: ${({ theme }) => theme.page.header.bg};
  border-bottom: ${border.width}px solid ${({ theme }) => theme.page.header.border};
  color: ${({ theme }) => theme.page.body.text};
`;

const Body = styled.main`
  background-color: ${({ theme }) => theme.page.body.bg};
  color: ${({ theme }) => theme.page.body.text};
`;

const Footer = styled.footer`
  background-color: ${({ theme }) => theme.page.footer.bg};
  border-top: ${border.width}px solid ${({ theme }) => theme.page.footer.border};
  color: ${({ theme }) => theme.page.body.text};
`;

interface KitAppLayoutClassNameProps extends PropsWithChildren {
  className?: string;
  role?: string;
  t?: TranslateFunc;
}

const filterDisabled = (el: HTMLElement) => !el.hasAttribute('disabled');
const filterNotTabbable = (el: HTMLElement) => !(el.tabIndex === -1);
const filterNotVisible = (el: HTMLElement) => !(el.offsetParent === null);

const getFocusableElements = (wrappers: PageWrapper[]): HTMLElement[] => {
  let queryString: string = wrappers.reduce((acc: string, curr: PageWrapper) => {
    return `${acc} ${curr} a, ${curr} input, ${curr} textarea, ${curr} select, ${curr} details, ${curr} div[role=button], ${curr} button,`;
  }, '');
  queryString = queryString.replace(/.$/, '');
  const nodeListOfElements = document.querySelectorAll<HTMLElement>(queryString);
  const arrayOfElements = Array.from(nodeListOfElements);
  return arrayOfElements.filter(filterDisabled).filter(filterNotTabbable).filter(filterNotVisible);
};

const KitAppLayoutHeader: React.FC<Omit<KitAppLayoutClassNameProps, 'role'>> = ({
  className,
  children,
  t,
}) => {
  const skipToContent = t ? t('kit:skip_to_content') : 'Skip to content';

  const onClick = () => {
    const keyboardFocusableElements = getFocusableElements([PageWrapper.MAIN, PageWrapper.FOOTER]);
    if (keyboardFocusableElements.length > 0) {
      keyboardFocusableElements[0].focus();
    } else {
      const headerKeyboardFocusableElements = getFocusableElements([PageWrapper.HEADER]);
      if (headerKeyboardFocusableElements.length > 0) {
        if (headerKeyboardFocusableElements[1]) {
          headerKeyboardFocusableElements[1].focus();
        } else {
          headerKeyboardFocusableElements[0].blur();
        }
      }
    }
  };

  return (
    <Header className={className}>
      <KitSkipToContent onClick={onClick}>{skipToContent}</KitSkipToContent>
      {children}
    </Header>
  );
};

const KitAppLayoutBody: FC<Omit<KitAppLayoutClassNameProps, 't'>> = ({
  children,
  className,
  role,
}) => {
  return (
    <Body className={className} role={role}>
      {children}
    </Body>
  );
};

const KitAppLayoutFooter: FC<Pick<KitAppLayoutClassNameProps, 'className' | 'children'>> = ({
  className,
  children,
}) => {
  return <Footer className={className}>{children}</Footer>;
};

export interface KitAppLayoutProps {
  children: ReactNode | ReactNode[];
}

type KitAppLayoutDef<T> = FC<T> & {
  Header: FC<Omit<KitAppLayoutClassNameProps, 'role'>>;
  Body: FC<PropsWithChildren>;
  Footer: FC<PropsWithChildren>;
};

const KitAppLayout: KitAppLayoutDef<KitAppLayoutProps> = ({ children }) => {
  return <Container>{children}</Container>;
};

KitAppLayout.Header = KitAppLayoutHeader;
KitAppLayout.Body = KitAppLayoutBody;
KitAppLayout.Footer = KitAppLayoutFooter;

export default KitAppLayout;
